import { FormEvent, useState } from 'react';
import {
    ABILITY_MARKER_FIELDS,
    AVAILABLE_COLUMNS,
    DEFAULT_COLUMNS,
    DEFAULT_PILL_FILTERS,
    STANDARD_FILTERS,
} from './reportRegistry';

export type DrawerTab = 'columns' | 'filters' | 'custom';

export type CustomFieldDraft = {
    name: string;
    purpose: 'operational' | 'scoring';
    scores: string[];
    type: string;
    visibility: string;
    context: string;
};

export type ManagedFilter = {
    key: string;
    label: string;
    group: 'Standard' | 'Report-specific' | 'Custom';
    applied: boolean;
    pill: boolean;
};

export type ColumnSetup = {
    standard: { key: string; label: string }[];
    /** Standard columns taken off the table. + puts one back. */
    availableStandard?: { key: string; label: string }[];
    onShowColumn?: (key: string) => void;
    onHideColumn?: (key: string) => void;
    filters: string[];
    custom: { id: number; label: string; showAsColumn: number; showAsFilter: number; dataType: string }[];
    filterItems?: ManagedFilter[];
    onFilterApplied?: (key: string, applied: boolean) => void;
    onFilterPill?: (key: string, pill: boolean) => void;
    markerCategories?: { value: string; label: string }[];
    saving?: boolean;
    error?: string;
    onCreate: (input: { label: string; dataType: string; visibility: string; optionsText: string; markerType?: string }) => Promise<void>;
    onChangeVisibility: (id: number, patch: { showAsColumn?: boolean; showAsFilter?: boolean }) => Promise<void>;
};

type Props = {
    reportLabel?: string;
    shownColumns?: string[];
    activeTab: DrawerTab;
    customFields: CustomFieldDraft[];
    columnSetup?: ColumnSetup;
    onTabChange: (tab: DrawerTab) => void;
    onClose: () => void;
    onCreateField: (field: CustomFieldDraft) => void;
};

const FIELD_TYPES = ['Text (short)', 'Number', 'Date', 'Single-select', 'Multi-select', 'Yes / No'];
const VISIBILITY = ['Column + Filter', 'Column only', 'Filter only', 'Hidden (only in upload)'];
const BURNER_SCORES = [
    { value: 'Alma Mater Pride', hint: 'Loyalty, affinity, engagement with alma mater' },
    { value: 'Marketability Quotient', hint: 'External visibility, career weight, story-worthiness' },
    { value: 'Vibrancy', hint: 'Overall activity level across touchpoints' },
    { value: 'Donor', hint: 'Likelihood or history of donating' },
    { value: 'Mentor', hint: 'Willingness / history of mentoring students' },
    { value: 'Recruiter', hint: 'Ability to recruit / hire from the institute' },
];

function matchesColumn(label: string, query: string) {
    const text = query.trim().toLowerCase();
    return !text || label.toLowerCase().includes(text);
}

function ColumnPicker({
    columnSetup,
    query,
    onQuery,
}: {
    columnSetup: ColumnSetup;
    query: string;
    onQuery: (value: string) => void;
}) {
    const availableStandard = (columnSetup.availableStandard || []).filter((column) => matchesColumn(column.label, query));
    const availableCustom = columnSetup.custom.filter((field) => !Number(field.showAsColumn) && matchesColumn(field.label, query));
    const shownStandard = columnSetup.standard.filter((column) => matchesColumn(column.label, query));
    const shownCustom = columnSetup.custom.filter((field) => Number(field.showAsColumn) === 1 && matchesColumn(field.label, query));
    const canMove = Boolean(columnSetup.onHideColumn && columnSetup.onShowColumn);

    return (
        <div className="cr-picker">
            <div className="cr-picker-col">
                <h6>Available</h6>
                <div className="cr-picker-search">
                    <input placeholder="Search fields…" value={query} onChange={(event) => onQuery(event.target.value)} />
                </div>
                <div className="cr-picker-list">
                    {availableStandard.length === 0 && availableCustom.length === 0 && (
                        <p className="cr-hint px-3">Remove a column with × and it shows up here. + puts it back on the table.</p>
                    )}
                    {availableStandard.length > 0 && <div className="cr-picker-group">Standard fields</div>}
                    {availableStandard.map((column) => (
                        <button
                            type="button"
                            className="cr-picker-item"
                            key={column.key}
                            aria-label={`Add ${column.label}`}
                            onClick={() => columnSetup.onShowColumn?.(column.key)}>
                            <span>{column.label}</span>
                            <i className="bi bi-plus-lg"></i>
                        </button>
                    ))}
                    {availableCustom.length > 0 && <div className="cr-picker-group">Custom fields</div>}
                    {availableCustom.map((field) => (
                        <button
                            type="button"
                            className="cr-picker-item"
                            key={field.id}
                            aria-label={`Add ${field.label}`}
                            onClick={() => columnSetup.onChangeVisibility(field.id, { showAsColumn: true })}>
                            <span>
                                {field.label} <span className="cr-tag cr-tag-custom">CUSTOM</span>
                            </span>
                            <i className="bi bi-plus-lg"></i>
                        </button>
                    ))}
                </div>
            </div>
            <div className="cr-picker-col">
                <h6>Shown in table</h6>
                <div className="cr-picker-list">
                    {shownStandard.map((column) => (
                        <div className="cr-chosen" key={column.key}>
                            {canMove ? null : <i className="bi bi-lock"></i>}
                            <span className="cr-chosen-name">{column.label}</span>
                            {canMove && (
                                <button
                                    type="button"
                                    className="cr-icon-btn"
                                    aria-label={`Remove ${column.label}`}
                                    onClick={() => columnSetup.onHideColumn?.(column.key)}>
                                    <i className="bi bi-x"></i>
                                </button>
                            )}
                        </div>
                    ))}
                    {shownCustom.map((field) => (
                        <div className="cr-chosen" key={field.id}>
                            <span className="cr-chosen-name">
                                {field.label} <span className="cr-tag cr-tag-custom">CUSTOM</span>
                            </span>
                            <button
                                type="button"
                                className="cr-icon-btn"
                                aria-label={`Remove ${field.label}`}
                                onClick={() => columnSetup.onChangeVisibility(field.id, { showAsColumn: false })}>
                                <i className="bi bi-x"></i>
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const CustomizeViewDrawer = ({
    reportLabel = 'Ability Markers',
    shownColumns = DEFAULT_COLUMNS,
    activeTab,
    customFields,
    columnSetup,
    onTabChange,
    onClose,
    onCreateField,
}: Props) => {
    const [showCreate, setShowCreate] = useState(false);
    const [purpose, setPurpose] = useState<'operational' | 'scoring'>('operational');
    const [scores, setScores] = useState<string[]>([]);
    const [scoreError, setScoreError] = useState('');
    const [fieldType, setFieldType] = useState(FIELD_TYPES[0]);
    const [markerType, setMarkerType] = useState('workProfile');
    const [createError, setCreateError] = useState('');
    const [filterQuery, setFilterQuery] = useState('');
    const [columnQuery, setColumnQuery] = useState('');

    const extraFilters = ABILITY_MARKER_FIELDS.filter((f) => !f.popular).map((f) => f.label);
    const standardAvail = STANDARD_FILTERS.filter((f) => !DEFAULT_PILL_FILTERS.includes(f));

    const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        const name = (form.elements.namedItem('cfName') as HTMLInputElement).value.trim();
        if (!name) return;
        const visibility = (form.elements.namedItem('cfVisibility') as HTMLSelectElement).value;
        const optionsText = ((form.elements.namedItem('cfOptions') as HTMLTextAreaElement | null)?.value || '').trim();
        if (columnSetup) {
            if ((fieldType === 'Single-select' || fieldType === 'Multi-select') && !optionsText) {
                setCreateError('Add at least one option, one per line.');
                return;
            }
            setCreateError('');
            try {
                await columnSetup.onCreate({ label: name, dataType: fieldType, visibility, optionsText, markerType });
                setShowCreate(false);
                setPurpose('operational');
                setScores([]);
                setScoreError('');
                setFieldType(FIELD_TYPES[0]);
                form.reset();
                onTabChange('custom');
            } catch (err: any) {
                setCreateError(err?.data?.message || err?.message || 'Could not create the field.');
            }
            return;
        }
        if (purpose === 'scoring' && scores.length === 0) {
            setScoreError('Pick at least one burner score, or switch to Operational only.');
            return;
        }
        onCreateField({
            name,
            purpose,
            scores,
            type: fieldType,
            visibility,
            context: (form.elements.namedItem('cfMeaning') as HTMLTextAreaElement).value.trim(),
        });
        setShowCreate(false);
        setPurpose('operational');
        setScores([]);
        setScoreError('');
        form.reset();
        onTabChange('custom');
    };

    const toggleScore = (value: string) => {
        setScores((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));
        setScoreError('');
    };

    return (
        <>
            <div className="cr-drawer-backdrop" onClick={onClose}></div>
            <aside className="cr-drawer" role="dialog" aria-labelledby="cr-drawer-title">
                <div className="cr-drawer-head">
                    <div>
                        <h5 id="cr-drawer-title">Customize View — {reportLabel}</h5>
                        <p>
                            {columnSetup
                                ? 'Remove a column with ×. It moves to Available, and + puts it back on the table. Custom fields you create are added to the sample file.'
                                : 'UI only for now. Save view and custom fields will persist in a later slice.'}
                        </p>
                    </div>
                    <button type="button" className="cr-btn cr-btn-ghost cr-icon-btn" onClick={onClose} aria-label="Close">
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <div className="cr-drawer-tabs">
                    <button type="button" className={activeTab === 'columns' ? 'active' : ''} onClick={() => onTabChange('columns')}>
                        <i className="bi bi-layout-three-columns me-1"></i>Columns
                    </button>
                    <button type="button" className={activeTab === 'filters' ? 'active' : ''} onClick={() => onTabChange('filters')}>
                        <i className="bi bi-funnel me-1"></i>Filters
                    </button>
                    <button type="button" className={activeTab === 'custom' ? 'active' : ''} onClick={() => onTabChange('custom')}>
                        <i className="bi bi-plus-square me-1"></i>Custom Fields
                    </button>
                </div>
                <div className="cr-drawer-body">
                    {activeTab === 'columns' && columnSetup && (
                        <ColumnPicker columnSetup={columnSetup} query={columnQuery} onQuery={setColumnQuery} />
                    )}

                    {activeTab === 'columns' && !columnSetup && (
                        <div className="cr-picker">
                            <div className="cr-picker-col">
                                <h6>Available</h6>
                                <div className="cr-picker-search">
                                    <input placeholder="Search fields…" />
                                </div>
                                <div className="cr-picker-list">
                                    <div className="cr-picker-group">Standard fields</div>
                                    {AVAILABLE_COLUMNS.map((c) => (
                                        <div className="cr-picker-item" key={c}>
                                            <span>{c}</span>
                                            <i className="bi bi-plus-lg"></i>
                                        </div>
                                    ))}
                                    {customFields.length > 0 && (
                                        <>
                                            <div className="cr-picker-group">Custom fields</div>
                                            {customFields.map((c) => (
                                                <div className="cr-picker-item" key={c.name}>
                                                    <span>
                                                        {c.name} <span className="cr-tag cr-tag-custom">CUSTOM</span>
                                                    </span>
                                                    <i className="bi bi-plus-lg"></i>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="cr-picker-col">
                                <h6>Shown in table</h6>
                                <div className="cr-picker-list">
                                    {shownColumns.map((c) => (
                                        <div className="cr-chosen" key={c}>
                                            <i className="bi bi-grip-vertical"></i>
                                            <span className="cr-chosen-name">{c}</span>
                                            <i className="bi bi-x"></i>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'filters' && columnSetup?.filterItems && (
                        <div className="cr-picker">
                            <div className="cr-picker-col">
                                <h6>Available filters</h6>
                                <div className="cr-picker-search">
                                    <input
                                        placeholder="Search filters…"
                                        value={filterQuery}
                                        onChange={(event) => setFilterQuery(event.target.value)}
                                    />
                                </div>
                                <div className="cr-picker-list">
                                    {(['Standard', 'Report-specific', 'Custom'] as const).map((group) => {
                                        const items = columnSetup.filterItems!.filter(
                                            (item) =>
                                                !item.applied &&
                                                item.group === group &&
                                                item.label.toLowerCase().includes(filterQuery.trim().toLowerCase())
                                        );
                                        if (!items.length) return null;
                                        return (
                                            <div key={group}>
                                                <div className="cr-picker-group">{group}</div>
                                                {items.map((item) => (
                                                    <button
                                                        type="button"
                                                        className="cr-picker-item"
                                                        key={item.key}
                                                        onClick={() => columnSetup.onFilterApplied?.(item.key, true)}>
                                                        <span>{item.label}</span>
                                                        <i className="bi bi-plus-lg"></i>
                                                    </button>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="cr-picker-col">
                                <h6>Applied filters</h6>
                                <div className="cr-picker-list">
                                    {columnSetup.filterItems.filter((item) => item.applied).map((item) => (
                                        <div className="cr-chosen" key={item.key}>
                                            <span className="cr-chosen-name">{item.label}</span>
                                            <label className="form-check form-switch cr-pill-switch mb-0" title="Show this filter">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    checked={item.pill}
                                                    aria-label={`Show ${item.label}`}
                                                    onChange={(event) => columnSetup.onFilterPill?.(item.key, event.target.checked)}
                                                />
                                            </label>
                                            <button
                                                type="button"
                                                className="cr-icon-btn"
                                                aria-label={`Remove ${item.label}`}
                                                onClick={() => columnSetup.onFilterApplied?.(item.key, false)}>
                                                <i className="bi bi-x"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <p className="cr-hint px-3">
                                    Turning the switch off does not remove the filter. It stays on the report. Use × to take it off.
                                </p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'filters' && columnSetup && !columnSetup.filterItems && (
                        <div className="cr-picker">
                            <div className="cr-picker-col">
                                <h6>Available filters</h6>
                                <div className="cr-picker-list">
                                    {columnSetup.custom
                                        .filter((field) => !Number(field.showAsFilter))
                                        .map((field) => (
                                            <button
                                                type="button"
                                                className="cr-picker-item"
                                                key={field.id}
                                                onClick={() => columnSetup.onChangeVisibility(field.id, { showAsFilter: true })}>
                                                <span>{field.label}</span>
                                                <i className="bi bi-plus-lg"></i>
                                            </button>
                                        ))}
                                </div>
                            </div>
                            <div className="cr-picker-col">
                                <h6>Applied filters</h6>
                                <div className="cr-picker-list">
                                    {columnSetup.filters.map((filter) => (
                                        <div className="cr-chosen" key={filter}>
                                            <span className="cr-chosen-name">{filter}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'filters' && !columnSetup && (
                        <>
                            <div className="cr-picker">
                                <div className="cr-picker-col">
                                    <h6>Available filters</h6>
                                    <div className="cr-picker-search">
                                        <input placeholder="Search filters…" />
                                    </div>
                                    <div className="cr-picker-list">
                                        <div className="cr-picker-group">Standard filters</div>
                                        {standardAvail.map((f) => (
                                            <div className="cr-picker-item" key={f}>
                                                <span>{f}</span>
                                                <i className="bi bi-plus-lg"></i>
                                            </div>
                                        ))}
                                        <div className="cr-picker-group">Report-specific (Ability)</div>
                                        {extraFilters.map((f) => (
                                            <div className="cr-picker-item" key={f}>
                                                <span>{f}</span>
                                                <i className="bi bi-plus-lg"></i>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="cr-picker-col">
                                    <h6>Applied filters</h6>
                                    <div className="cr-picker-list">
                                        {DEFAULT_PILL_FILTERS.map((f) => (
                                            <div className="cr-chosen" key={f}>
                                                <i className="bi bi-grip-vertical"></i>
                                                <span className="cr-chosen-name">{f}</span>
                                                <label className="form-check form-switch cr-pill-switch mb-0" title="Keep this filter on the top bar">
                                                    <input className="form-check-input" type="checkbox" role="switch" defaultChecked aria-label={`Keep ${f} on the top bar`} />
                                                </label>
                                                <i className="bi bi-x"></i>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <p className="cr-hint">
                                <i className="bi bi-info-circle me-1"></i>
                                Turning the switch off does not remove the filter. Use × to take it off.
                            </p>
                        </>
                    )}

                    {activeTab === 'custom' && columnSetup && (
                        <>
                            <div className="cr-cf-head">
                                <div>
                                    <div className="cr-cf-title">Custom fields for this report</div>
                                    <p className="cr-hint mb-0">
                                        A new field is added to the sample file. Choose Column to show it in the table, and Filter to show it in the filter list.
                                    </p>
                                </div>
                                <button type="button" className="cr-btn cr-btn-navy cr-btn-sm" onClick={() => setShowCreate(true)}>
                                    <i className="bi bi-plus-lg me-1"></i>Create custom field
                                </button>
                            </div>
                            {columnSetup.error && <div className="cr-error">{columnSetup.error}</div>}
                            {columnSetup.custom.length === 0 ? (
                                <div className="cr-empty">
                                    <i className="bi bi-plus-square"></i>
                                    <div>No custom fields yet.</div>
                                </div>
                            ) : (
                                columnSetup.custom.map((field) => (
                                    <div className="cr-cf-row" key={field.id}>
                                        <div>
                                            <div className="cr-cf-name">{field.label}</div>
                                            <div className="cr-hint">
                                                {Number(field.showAsColumn) === 1 ? 'Shown in table' : 'Not a column'}
                                                {' · '}
                                                {Number(field.showAsFilter) === 1 ? 'Filter' : 'Not a filter'}
                                            </div>
                                        </div>
                                        <span className="cr-type">{field.dataType}</span>
                                    </div>
                                ))
                            )}
                        </>
                    )}

                    {activeTab === 'custom' && !columnSetup && (
                        <>
                            <div className="cr-cf-head">
                                <div>
                                    <div className="cr-cf-title">Custom fields for this report</div>
                                    <p className="cr-hint mb-0">
                                        A custom field becomes a column, a filter, and an upload-template column. Values will join{' '}
                                        <code>profile_markers</code> in a later slice.
                                    </p>
                                </div>
                                <button type="button" className="cr-btn cr-btn-navy cr-btn-sm" onClick={() => setShowCreate(true)}>
                                    <i className="bi bi-plus-lg me-1"></i>Create custom field
                                </button>
                            </div>
                            {customFields.length === 0 ? (
                                <div className="cr-empty">
                                    <i className="bi bi-plus-square"></i>
                                    <div>No custom fields yet.</div>
                                    <span>Click Create custom field to add one. Not saved to the database yet.</span>
                                </div>
                            ) : (
                                customFields.map((c) => (
                                    <div className="cr-cf-row" key={c.name}>
                                        <div>
                                            <div className="cr-cf-name">{c.name}</div>
                                            <div className="cr-hint">Session only · {c.visibility}</div>
                                            <div className="mt-1">
                                                {c.purpose === 'operational' ? (
                                                    <span className="cr-tag">Operational only</span>
                                                ) : (
                                                    <span className="cr-tag cr-tag-navy">Feeds: {c.scores.join(', ')}</span>
                                                )}
                                            </div>
                                        </div>
                                        <span className="cr-type">{c.type}</span>
                                    </div>
                                ))
                            )}
                        </>
                    )}
                </div>
                <div className="cr-drawer-foot">
                    <button type="button" className="cr-link">
                        <i className="bi bi-arrow-counterclockwise me-1"></i>Reset to default view
                    </button>
                    <div className="d-flex gap-2">
                        <button type="button" className="cr-btn cr-btn-ghost cr-btn-sm" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="button" className="cr-btn cr-btn-navy cr-btn-sm" onClick={onClose}>
                            Save View
                        </button>
                    </div>
                </div>
            </aside>

            {showCreate && (
                <>
                    <div className="cr-modal-backdrop" onClick={() => setShowCreate(false)}></div>
                    <div className="cr-modal" role="dialog" aria-labelledby="cr-cf-title">
                        <form onSubmit={handleCreate}>
                            <div className="cr-modal-head">
                                <h5 id="cr-cf-title">Create Custom Field</h5>
                                <button type="button" className="cr-btn cr-btn-ghost cr-icon-btn" onClick={() => setShowCreate(false)} aria-label="Close">
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                            <div className="cr-modal-body">
                                <label className="cr-label">Field name</label>
                                <input className="form-control" name="cfName" placeholder="e.g. Homecoming '24 attendance level" />
                                <p className="cr-hint">Will appear as a column header and filter label.</p>

                                <label className="cr-label mt-3">
                                    Purpose of this field <span className="cr-req">*</span>
                                </label>
                                <p className="cr-hint">
                                    <i className="bi bi-lightbulb me-1"></i>
                                    Tell burner analysis how to treat this field.
                                </p>
                                <label className={`cr-purpose${purpose === 'operational' ? ' selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="cfPurpose"
                                        checked={purpose === 'operational'}
                                        onChange={() => {
                                            setPurpose('operational');
                                            setScoreError('');
                                        }}
                                    />
                                    <span>
                                        <strong>Operational only</strong>
                                        <small>For tracking, communication, segmentation or reporting. Does not influence any burner score.</small>
                                    </span>
                                </label>
                                <label className={`cr-purpose${purpose === 'scoring' ? ' selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="cfPurpose"
                                        checked={purpose === 'scoring'}
                                        onChange={() => setPurpose('scoring')}
                                    />
                                    <span>
                                        <strong>Contributes to burner analysis</strong>
                                        <small>This field carries a signal that should feed one or more of the six burner scores.</small>
                                    </span>
                                </label>

                                {purpose === 'scoring' && (
                                    <div className="mt-3">
                                        <label className="cr-label">
                                            Which scores does this field contribute to? <span className="cr-req">*</span>
                                        </label>
                                        <div className="cr-scores">
                                            {BURNER_SCORES.map((s) => (
                                                <label key={s.value}>
                                                    <input
                                                        type="checkbox"
                                                        checked={scores.includes(s.value)}
                                                        onChange={() => toggleScore(s.value)}
                                                    />
                                                    <span>
                                                        <span className="cr-score-name">{s.value}</span>
                                                        <span className="cr-score-hint">{s.hint}</span>
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                        {scoreError && <div className="cr-error">{scoreError}</div>}
                                    </div>
                                )}

                                <label className="cr-label mt-3">Additional context (optional)</label>
                                <textarea
                                    className="form-control"
                                    name="cfMeaning"
                                    rows={2}
                                    placeholder="e.g. Higher values indicate stronger engagement."
                                />

                                <div className="row mt-3">
                                    <div className="col-md-6 mb-3">
                                        <label className="cr-label">Field type</label>
                                        <select className="form-select" name="cfType" value={fieldType} onChange={(event) => setFieldType(event.target.value)}>
                                            {FIELD_TYPES.map((t) => (
                                                <option key={t}>{t}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="cr-label">Default visibility</label>
                                        <select className="form-select" name="cfVisibility">
                                            {VISIBILITY.map((t) => (
                                                <option key={t}>{t}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {(fieldType === 'Single-select' || fieldType === 'Multi-select') && (
                                    <div className="mt-3">
                                        <label className="cr-label">Options</label>
                                        <textarea className="form-control" name="cfOptions" rows={3} placeholder={'One option per line'} />
                                    </div>
                                )}
                                {columnSetup?.markerCategories && (
                                    <div className="mt-3">
                                        <label className="cr-label">Marker group</label>
                                        <select className="form-select" value={markerType} onChange={(event) => setMarkerType(event.target.value)}>
                                            {columnSetup.markerCategories.map((group) => (
                                                <option key={group.value} value={group.value}>
                                                    {group.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className="cr-callout">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Adding this field will add a new column to the upload template for this report. Upload wiring comes in a
                                    later slice.
                                </div>
                            </div>
                            <div className="cr-modal-foot">
                                <button type="button" className="cr-btn cr-btn-ghost" onClick={() => setShowCreate(false)}>
                                    Cancel
                                </button>
                                {createError && <div className="cr-error mb-2">{createError}</div>}
                                <button type="submit" className="cr-btn cr-btn-navy" disabled={columnSetup?.saving}>
                                    Create Field
                                </button>
                            </div>
                        </form>
                    </div>
                </>
            )}
        </>
    );
};

export default CustomizeViewDrawer;
